import intl from "react-intl-universal";
import { useHistory } from "react-router-dom";
import {
  getAdditionalCurrencyCount,
  getPrimaryBalance,
  formatBaasAvailableWithLimit,
} from "../../../../../Services/baasAccountsListApi";
import { getInternalTransferRoleLabelKey } from "../../../../../Services/baasInternalTransfersApi";
import {
  isBaasOtcVirtualAccount,
  isBaasSegregationWalletAccount,
} from "../../../../../Services/baasVirtualShells";
import { formatMonetaryValue } from "../../../../../Utils/formatters";
import { buildBaasAccountDetailPath } from "../../constants";

const BORDER_CARD =
  "rounded-xl border border-gray-200 bg-white dark:border-brand-dark-border dark:bg-brand-dark-surface";

/** Same summary as Contas Shells operacionais cards (primary available + extra currencies). */
const formatBalanceSummary = (balances = []) => {
  const primary = getPrimaryBalance(balances);
  if (!primary) return intl.get("baas.accounts.noBalances");

  const primaryLabel = formatBaasAvailableWithLimit(primary);
  const extraCount = getAdditionalCurrencyCount(balances);
  if (extraCount === 0) return primaryLabel;

  return `${primaryLabel} · ${intl.get("baas.accounts.additionalCurrencies", {
    count: extraCount,
  })}`;
};

const roleOrTypeLabel = (account) => {
  const roleKey = getInternalTransferRoleLabelKey(account?.role);
  if (roleKey) {
    // OTC role label is remapped via i18n to WALLET; keep role key lookup.
    return intl.get(roleKey);
  }
  // OTC is internal — display as WALLET.
  if (isBaasOtcVirtualAccount(account) || isBaasSegregationWalletAccount(account)) {
    return intl.get("baas.accounts.type.wallet") || "WALLET";
  }
  const type = String(account?.type || "").trim();
  if (type === "otc" || type === "otc_virtual") {
    return intl.get("baas.accounts.type.wallet") || "WALLET";
  }
  if (type) {
    const key = `baas.accounts.type.${type}`;
    const label = intl.get(key);
    if (label && label !== key) return label;
  }
  return account?.role || account?.type || intl.get("baas.accounts.type.virtual");
};

const AccountRows = ({ rows, emptyKey }) => {
  const history = useHistory();

  if (rows.length === 0) {
    return (
      <div className={`${BORDER_CARD} app-empty-state`}>
        <p className="text-sm text-gray-500 dark:text-gray-400">{intl.get(emptyKey)}</p>
      </div>
    );
  }

  return (
    <div className={`${BORDER_CARD} divide-y divide-gray-100 dark:divide-brand-dark-border`}>
      {rows.map((account) => (
        <button
          key={account.id}
          type="button"
          className="flex w-full min-w-0 items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-gray-50/80 dark:hover:bg-brand-dark-elevated/40 sm:px-5"
          onClick={() => history.push(buildBaasAccountDetailPath(account.id))}
        >
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[#a66f1d] dark:text-brand-gold-mid">
              {roleOrTypeLabel(account)}
            </p>
            <p className="mt-0.5 truncate text-sm font-semibold text-brand-dark dark:text-gray-100">
              {account.name || account.alias || account.id}
            </p>
            <p className="truncate font-mono text-xs text-gray-500 dark:text-gray-400">
              {account.id}
            </p>
            <div className="mt-2 border-t border-gray-100 pt-2 dark:border-brand-dark-border">
              <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                {intl.get("baas.accounts.columns.balances")}
              </p>
              <p
                className="mt-0.5 truncate text-xs font-semibold tabular-nums text-brand-dark dark:text-gray-100"
                title={formatBalanceSummary(account.balances)}
              >
                {formatBalanceSummary(account.balances)}
              </p>
            </div>
          </div>
          <span className="shrink-0 text-xs font-semibold text-[#a66f1d] dark:text-brand-gold-mid">
            {intl.get("baas.accounts.detailAction")} →
          </span>
        </button>
      ))}
    </div>
  );
};

const RelatedSection = ({ titleKey, subtitleKey, emptyKey, rows, loading, loadingKey }) => (
  <section className="min-w-0 space-y-3">
    <div>
      <p className="dashboard-section-label mb-1">{intl.get(titleKey)}</p>
      <p className="text-sm text-gray-500 dark:text-gray-400">{intl.get(subtitleKey)}</p>
    </div>

    {loading ? (
      <div className={`${BORDER_CARD} px-4 py-6 text-sm text-gray-500 dark:text-gray-400`}>
        {intl.get(loadingKey)}
      </div>
    ) : (
      <AccountRows rows={rows} emptyKey={emptyKey} />
    )}
  </section>
);

/**
 * Related instance operation shells + segregation wallets for the account’s entity.
 * Shells operacionais only on the standalone instance Root account detail (Contas).
 * Hidden when the detail is embedded on /baas/root — that page already shows the cards.
 * Contas virtuais omitted on exit-rail virtuals and instance shells (reserves).
 */
export const BaasAccountRelatedVirtualAccountsPanel = ({
  operationAccounts = [],
  virtualAccounts = [],
  /** @deprecated Prefer operationAccounts + virtualAccounts */
  accounts = [],
  loading = false,
  /** When false, omit Shells operacionais entirely (non-root account detail). */
  showOperationShells = false,
  /** When false, omit Contas virtuais (already viewing an OTC/WALLET virtual). */
  showVirtualAccounts = true,
  onCreateVirtualClick = null,
}) => {
  const operationRows = Array.isArray(operationAccounts)
    ? operationAccounts
    : [];
  const virtualRows = Array.isArray(virtualAccounts) ? virtualAccounts : [];

  // Backward compatible: if only `accounts` is passed, treat as combined (show under virtuais).
  const legacyRows =
    operationRows.length === 0 && virtualRows.length === 0 && Array.isArray(accounts)
      ? accounts
      : [];

  if (!showOperationShells && !showVirtualAccounts) return null;

  return (
    <div className="min-w-0 space-y-6">
      {showOperationShells ? (
        <RelatedSection
          titleKey="baas.accounts.detail.operationAccountsTitle"
          subtitleKey="baas.accounts.detail.operationAccountsSubtitle"
          emptyKey="baas.accounts.detail.operationAccountsEmpty"
          loadingKey="baas.accounts.detail.operationAccountsLoading"
          rows={operationRows}
          loading={loading}
        />
      ) : null}

      {showVirtualAccounts ? (
        <section className="min-w-0 space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="dashboard-section-label mb-1">
                {intl.get("baas.accounts.detail.virtualAccountsTitle")}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {intl.get("baas.accounts.detail.virtualAccountsSubtitle")}
              </p>
            </div>
            {typeof onCreateVirtualClick === "function" ? (
              <button
                type="button"
                className="btn-primary w-auto shrink-0 inline-flex items-center gap-2 !text-xs"
                onClick={onCreateVirtualClick}
              >
                {intl.get("baas.accounts.virtualCreate.submit")}
              </button>
            ) : null}
          </div>

          {loading ? (
            <div className={`${BORDER_CARD} px-4 py-6 text-sm text-gray-500 dark:text-gray-400`}>
              {intl.get("baas.accounts.detail.virtualAccountsLoading")}
            </div>
          ) : (
            <AccountRows
              rows={virtualRows.length ? virtualRows : legacyRows}
              emptyKey="baas.accounts.detail.virtualAccountsEmpty"
            />
          )}
        </section>
      ) : null}
    </div>
  );
};

export default BaasAccountRelatedVirtualAccountsPanel;
